import { componentTypography } from '@/theme/componentTypography';
import { r, s, typographyTokens } from '@/theme/tokens';
import { filterTokens } from '@/ui/composites/filters/filterTokens';
import { overlayScrollbarSx } from '@/ui/primitives/overlayScrollbarSx';
import type { SxProps, Theme } from '@mui/material/styles';

const resolveTypography = (
  theme: Theme,
  role: keyof typeof typographyTokens,
): { fontSize: string | number; fontWeight: number; lineHeight: string | number } => {
  const token = typographyTokens[role];
  const typography = theme.typography[token.variant];
  const fallback = theme.typography.body1;
  return {
    fontSize: typography.fontSize ?? fallback.fontSize ?? theme.typography.fontSize,
    fontWeight: token.fontWeight,
    lineHeight: typography.lineHeight ?? fallback.lineHeight ?? 1.5,
  };
};

type SxObject = Record<string, unknown>;

const isSxObject = (value: unknown): value is SxObject =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const resolveSxObject = (sx: SxProps<Theme>, theme: Theme): SxObject => {
  if (!sx) return {};
  const resolved: unknown = typeof sx === 'function' ? sx(theme) : sx;
  if (Array.isArray(resolved)) {
    return resolved.reduce<SxObject>((acc, item) => {
      if (isSxObject(item)) {
        return { ...acc, ...item };
      }
      return acc;
    }, {});
  }
  if (isSxObject(resolved)) {
    return resolved;
  }
  return {};
};

export const filterTypographyRole: keyof typeof typographyTokens = 'body';
export const filterDropdownIndicatorIconSizePx = 22;
export const filterDropdownIndicatorRightInset = `calc(${s('sm')} + ${s('xs')} + ${s('xs')})`;
export const filterDropdownMaxWidth = 'min(560px, calc(100vw - 32px))';
export const checkboxPopoverFontSize = '13px';
export const filterPopoverInsetBackgroundColor = 'var(--surface-inset)';
export const dropdownFiltersPopoverControlWidthPx = 180;
export const dropdownFiltersPopoverFieldGap = s('md');
export const dropdownFiltersPopoverFieldInputPadding = s('xs');
export const dropdownFiltersPopoverFontSizePx = 15;

export const filterMutedScrollbarSx: SxProps<Theme> = {
  scrollbarWidth: 'thin',
  scrollbarColor: (theme) => `${theme.app.colors.divider} transparent`,
  '&::-webkit-scrollbar': {
    width: s('xs'),
    height: s('xs'),
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: (theme) => theme.app.colors.divider,
    border: '1px solid transparent',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    backgroundColor: (theme) => theme.app.colors.border,
  },
};

export const mergeSx =
  (base: SxObject, extra: SxProps<Theme>): SxProps<Theme> =>
  (theme) => ({ ...base, ...resolveSxObject(extra, theme) });

export const mergeFilterSlotSx =
  (base: SxProps<Theme>, extra: SxObject): SxProps<Theme> =>
  (theme) => ({ ...resolveSxObject(base, theme), ...extra });

export const filterInputLabelSx: SxProps<Theme> = (theme) => resolveTypography(theme, 'caption');

export const filterInputSx: SxProps<Theme> = (theme) => ({
  '--flowqa-filter-text':
    theme.palette.mode === 'dark' ? theme.palette.common.white : theme.app.colors.text,
  '--flowqa-filter-placeholder':
    theme.palette.mode === 'dark' ? theme.palette.common.white : theme.app.colors.textMuted,
  '& .MuiInputBase-root': {
    ...resolveTypography(theme, filterTypographyRole),
    minHeight: filterTokens.controlMinHeight,
    height: filterTokens.controlMinHeight,
    alignItems: 'center',
    color: 'var(--flowqa-filter-text)',
  },
  '& .MuiOutlinedInput-root': {
    ...resolveTypography(theme, filterTypographyRole),
    minHeight: filterTokens.controlMinHeight,
    height: filterTokens.controlMinHeight,
    color: 'var(--flowqa-filter-text)',
  },
  '& .MuiPickersInputBase-root': {
    ...resolveTypography(theme, filterTypographyRole),
    minHeight: filterTokens.controlMinHeight,
    height: filterTokens.controlMinHeight,
    alignItems: 'center',
    color: 'var(--flowqa-filter-text)',
  },
  '& .MuiInputBase-input': {
    ...resolveTypography(theme, filterTypographyRole),
    minHeight: filterTokens.controlMinHeight,
    display: 'flex',
    alignItems: 'center',
    paddingTop: s('sm'),
    paddingBottom: s('sm'),
    color: 'var(--flowqa-filter-text)',
  },
  '& .MuiOutlinedInput-input': {
    ...resolveTypography(theme, filterTypographyRole),
    minHeight: filterTokens.controlMinHeight,
    display: 'flex',
    alignItems: 'center',
    paddingTop: s('sm'),
    paddingBottom: s('sm'),
    color: 'var(--flowqa-filter-text)',
  },
  '& .MuiPickersInputBase-input': {
    ...resolveTypography(theme, filterTypographyRole),
    minHeight: filterTokens.controlMinHeight,
    display: 'flex',
    alignItems: 'center',
    paddingTop: s('sm'),
    paddingBottom: s('sm'),
    paddingLeft: filterTokens.dateInputPaddingLeft,
    color: 'var(--flowqa-filter-text)',
  },
  '& .MuiInputBase-input::placeholder, & .MuiOutlinedInput-input::placeholder, & .MuiPickersInputBase-input::placeholder':
    {
      color: 'var(--flowqa-filter-placeholder)',
      opacity: 1,
    },
  '& .MuiInputLabel-root': { ...resolveTypography(theme, 'caption') },
});

export const filterSelectSx: SxProps<Theme> = (theme) => {
  const typography = resolveTypography(theme, filterTypographyRole);
  return {
    '--flowqa-filter-text':
      theme.palette.mode === 'dark' ? theme.palette.common.white : theme.app.colors.text,
    '--flowqa-filter-placeholder':
      theme.palette.mode === 'dark' ? theme.palette.common.white : theme.app.colors.textMuted,
    fontSize: typography.fontSize,
    fontWeight: typography.fontWeight,
    lineHeight: typography.lineHeight,
    '& .MuiInputBase-root': {
      ...typography,
      minHeight: filterTokens.controlMinHeight,
      height: filterTokens.controlMinHeight,
      alignItems: 'center',
      boxSizing: 'border-box',
      color: 'var(--flowqa-filter-text)',
    },
    '& .MuiOutlinedInput-root': {
      ...typography,
      minHeight: filterTokens.controlMinHeight,
      height: filterTokens.controlMinHeight,
      alignItems: 'center',
      boxSizing: 'border-box',
      color: 'var(--flowqa-filter-text)',
    },
    '& .MuiSelect-select': {
      ...typography,
      fontStyle: 'normal',
      minHeight: filterTokens.controlMinHeight,
      height: filterTokens.controlMinHeight,
      display: 'flex',
      alignItems: 'center',
      fontSize: typography.fontSize,
      lineHeight: typography.lineHeight,
      paddingTop: s('none'),
      paddingBottom: s('none'),
      paddingLeft: filterTokens.selectPaddingLeft,
      paddingRight: s('lg'),
      boxSizing: 'border-box',
      color: 'var(--flowqa-filter-text)',
    },
    '& .MuiSelect-select.MuiInputBase-input': { ...typography, fontStyle: 'normal' },
    '& .MuiSelect-select, & .MuiSelect-select.MuiInputBase-input, & .MuiSelect-select span': {
      fontSize: typography.fontSize,
      lineHeight: typography.lineHeight,
      fontWeight: typography.fontWeight,
      color: 'var(--flowqa-filter-text)',
    },
    '& .MuiOutlinedInput-root .MuiSelect-select': {
      height: filterTokens.controlMinHeight,
      minHeight: filterTokens.controlMinHeight,
    },
    '& .MuiSelect-select *': { ...typography, fontStyle: 'normal' },
    '& .MuiInputLabel-root': { ...resolveTypography(theme, 'caption') },
    '& .MuiSelect-icon': {
      fontSize: componentTypography.filters.iconFontSize,
      color: 'var(--flowqa-filter-placeholder)',
    },
  };
};

export const filterMenuItemSx: SxProps<Theme> = (theme) => ({
  ...resolveTypography(theme, filterTypographyRole),
});

export const filterMenuItemTextSx: SxProps<Theme> = (theme) =>
  resolveTypography(theme, filterTypographyRole);

export const filterIconSx: SxProps<Theme> = { fontSize: componentTypography.filters.iconFontSize };

export const buildFilterAutocompleteListboxSx = (
  optionRowMinHeight: string,
  maxVisibleOptions = 10,
): SxProps<Theme> => [
  {
    maxHeight: `calc(${String(maxVisibleOptions)} * ${optionRowMinHeight})`,
    overflowX: 'hidden',
    overflowY: 'auto',
  },
  overlayScrollbarSx,
  filterMutedScrollbarSx,
];

type FilterAutocompletePaperSxOptions = {
  fullWidth: boolean;
  minWidth: number | string;
};

type FilterPopoverPaperSxOptions = {
  backgroundColor?: string;
  maxWidth?: number | string;
  minWidth?: number | string;
  overflow?: string;
  radius?: string;
  width?: number | string;
};

type FilterSearchPopoverPaperSxOptions = {
  backgroundColor?: string;
  radius?: string;
  width?: number | string;
};

export const buildFilterPopoverPaperSx = ({
  backgroundColor = 'var(--surface-overlay)',
  maxWidth,
  minWidth = 0,
  overflow = 'hidden !important',
  radius = r('md'),
  width = 'auto',
}: FilterPopoverPaperSxOptions = {}): SxProps<Theme> => ({
  backgroundColor,
  backgroundImage: 'none',
  '--Paper-overlay': 'none',
  '--Paper-shadow': 'none',
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: `${radius} !important`,
  overflow,
  boxShadow: 'none',
  minWidth,
  width,
  maxWidth,
});

export const buildFilterSearchPopoverPaperSx = ({
  backgroundColor = filterPopoverInsetBackgroundColor,
  radius = r('sm'),
  width = 'min(90vw, 280px)',
}: FilterSearchPopoverPaperSxOptions = {}): SxProps<Theme> =>
  buildFilterPopoverPaperSx({
    backgroundColor,
    radius,
    width,
  });

export const checkboxPopoverPaperSx = buildFilterPopoverPaperSx({
  maxWidth: '90vw',
});

export const buildDropdownFiltersPopoverPaperSx = (controlRadius: string): SxProps<Theme> =>
  buildFilterPopoverPaperSx({
    backgroundColor: filterPopoverInsetBackgroundColor,
    radius: controlRadius,
    overflow: 'visible !important',
    minWidth: 0,
    width: 'fit-content',
  });

export const buildFilterPopoverRootSx = (
  radius = r('md'),
  overflow = 'hidden !important',
): SxProps<Theme> => ({
  '& .MuiPopover-paper': {
    borderRadius: `${radius} !important`,
    overflow,
  },
});

export const checkboxPopoverBodySx = {
  p: s('none'),
} as const;

export const checkboxPopoverSectionDividerSx = {
  borderTop: '1px solid',
  borderColor: 'divider',
  my: s('xxs'),
} as const;

export const checkboxPopoverSectionTitleSx: SxProps<Theme> = (theme) => ({
  px: s('md'),
  py: s('xxs'),
  fontSize: checkboxPopoverFontSize,
  lineHeight: 1.4,
  fontWeight: theme.typography.fontWeightRegular,
});

type BuildCheckboxPopoverOptionRowSxArgs = {
  disabled: boolean;
  minHeight: string;
};

export const buildCheckboxPopoverOptionRowSx = ({
  disabled,
  minHeight,
}: BuildCheckboxPopoverOptionRowSxArgs): SxProps<Theme> => ({
  px: s('md'),
  py: s('none'),
  pr: s('md'),
  width: '100%',
  minWidth: 0,
  minHeight,
  cursor: disabled ? 'not-allowed' : 'pointer',
  backgroundColor: 'transparent',
  '&:hover': disabled ? undefined : { backgroundColor: (theme) => theme.app.state.hover },
  '&:focus-visible': disabled
    ? undefined
    : {
        backgroundColor: (theme) => theme.app.state.hover,
        outline: (theme) => `2px solid ${theme.app.state.focus}`,
        outlineOffset: -2,
        '@media (forced-colors: active)': {
          forcedColorAdjust: 'auto',
          outline: '2px solid Highlight',
        },
      },
});

export const checkboxPopoverCheckboxSx = {
  p: s('none'),
  m: s('none'),
  mr: s('xs'),
} as const;

export const checkboxPopoverOptionLabelSx = {
  whiteSpace: 'nowrap',
  fontSize: checkboxPopoverFontSize,
  lineHeight: 1.4,
} as const;

export const checkboxPopoverEmptyTextSx = {
  px: s('md'),
  py: s('xs'),
  fontSize: checkboxPopoverFontSize,
  lineHeight: 1.4,
} as const;

export const buildDropdownFiltersPopoverStackSx = (controlRadius: string): SxProps<Theme> => ({
  borderRadius: `${controlRadius} !important`,
  overflow: 'visible',
});

export const dropdownFiltersPopoverBodySx = {
  pl: s('lg'),
  pr: s('lg'),
  py: s('lg'),
  width: 'fit-content',
  minWidth: 'fit-content',
} as const;

export const dropdownFiltersPopoverFieldsStackSx = {
  justifyContent: 'flex-start',
  alignItems: 'flex-end',
} as const;

export const searchFilterEndAdornmentSx = {
  mr: `calc(${s('sm')} * -1.5)`,
  pl: s('none'),
  ml: s('none'),
  gap: s('xs'),
  '&.MuiInputAdornment-positionEnd': {
    marginLeft: `-${s('sm')}`,
    marginRight: `calc(${s('sm')} * -1) !important`,
  },
} as const;

export const searchFilterStartAdornmentSx = {
  ml: `calc(${s('md')} * -1)`,
  mr: s('xxs'),
  display: 'inline-flex',
  alignItems: 'center',
} as const;

export const buildSearchFilterClearButtonSx = (filledClearIcon: boolean): SxProps<Theme> => ({
  opacity: 1,
  pointerEvents: 'auto',
  p: s('xxs'),
  width: 'auto',
  height: 'auto',
  ml: `-${s('xxs')}`,
  mr: s('xs'),
  color: filledClearIcon ? 'common.white' : 'action.active',
});

export const searchFilterTrailingIconSx = {
  fontSize: '0.95rem',
  transform: 'translateY(2px)',
} as const;

type BuildSearchFilterHeightSxArgs = {
  hasValue: boolean;
  resolvedHeight: number;
  resolvedPaddingLeft: string;
  resolvedPaddingRight: string;
  resolvedPaddingY: string;
};

export const buildSearchFilterHeightSx = ({
  hasValue,
  resolvedHeight,
  resolvedPaddingLeft,
  resolvedPaddingRight,
  resolvedPaddingY,
}: BuildSearchFilterHeightSxArgs): SxProps<Theme> => ({
  '&& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: (theme) => `${theme.app.colors.primary} !important`,
    borderWidth: '2px',
  },
  '&& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: (theme) => `${theme.app.colors.primary} !important`,
  },
  ...(hasValue
    ? {
        '&& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
          borderColor: (theme: Theme) => `${theme.app.colors.primary} !important`,
        },
      }
    : {}),
  '& .MuiInputBase-root': {
    minHeight: `${resolvedHeight}px !important`,
    height: `${resolvedHeight}px !important`,
    maxHeight: `${resolvedHeight}px !important`,
    alignItems: 'center',
    minWidth: 0,
    overflow: 'hidden',
  },
  '& .MuiInputBase-root.MuiOutlinedInput-root': {
    paddingTop: `${s('none')} !important`,
    paddingBottom: `${s('none')} !important`,
    minHeight: `${resolvedHeight}px !important`,
    height: `${resolvedHeight}px !important`,
  },
  '& .MuiInputBase-root.MuiOutlinedInput-root.MuiInputBase-sizeSmall': {
    minHeight: `${resolvedHeight}px !important`,
    height: `${resolvedHeight}px !important`,
  },
  '& .MuiOutlinedInput-root': {
    minHeight: `${resolvedHeight}px !important`,
    height: `${resolvedHeight}px !important`,
  },
  '& .MuiInputBase-input': {
    minHeight: `${resolvedHeight}px !important`,
    height: `${resolvedHeight}px !important`,
    minWidth: '0 !important',
    width: '100%',
    flex: '1 1 auto',
    paddingLeft: `${resolvedPaddingLeft} !important`,
    paddingInlineStart: `${resolvedPaddingLeft} !important`,
    paddingTop: `${resolvedPaddingY} !important`,
    paddingBottom: `${resolvedPaddingY} !important`,
    paddingRight: `${resolvedPaddingRight} !important`,
    lineHeight: 1,
    boxSizing: 'border-box',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  '& .MuiOutlinedInput-input': {
    minHeight: `${resolvedHeight}px !important`,
    height: `${resolvedHeight}px !important`,
    minWidth: '0 !important',
    width: '100%',
    flex: '1 1 auto',
    paddingLeft: `${resolvedPaddingLeft} !important`,
    paddingInlineStart: `${resolvedPaddingLeft} !important`,
    paddingTop: `${resolvedPaddingY} !important`,
    paddingBottom: `${resolvedPaddingY} !important`,
    paddingRight: `${resolvedPaddingRight} !important`,
    lineHeight: 1,
    boxSizing: 'border-box',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  '& .MuiOutlinedInput-root .MuiOutlinedInput-input.MuiInputBase-input': {
    paddingLeft: `${resolvedPaddingLeft} !important`,
    paddingInlineStart: `${resolvedPaddingLeft} !important`,
  },
});

export const searchFilterTypographySx: SxProps<Theme> = (theme) => ({
  '& .MuiInputBase-root, & .MuiOutlinedInput-root': {
    ...theme.typography.body1,
    fontSize: '13px',
    color: 'var(--flowqa-filter-text)',
  },
  '& .MuiInputBase-input, & .MuiOutlinedInput-input, & input': {
    ...theme.typography.body1,
    fontSize: '13px',
    color: 'var(--flowqa-filter-text)',
  },
  '& .MuiInputBase-input::placeholder, & .MuiOutlinedInput-input::placeholder, & input::placeholder':
    {
      ...theme.typography.body1,
      fontSize: '13px',
      color: 'var(--flowqa-filter-placeholder)',
      opacity: 1,
    },
  '& .MuiInputAdornment-root, & .MuiSvgIcon-root': {
    color: 'var(--flowqa-filter-placeholder)',
  },
});

export const buildFilterAutocompletePaperSx = ({
  fullWidth,
  minWidth,
}: FilterAutocompletePaperSxOptions): SxProps<Theme> => ({
  backgroundColor: 'var(--surface-page)',
  backgroundImage: 'none',
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: `${r('md')} !important`,
  boxShadow: 'none',
  clipPath: `inset(0 round ${r('md')})`,
  maxWidth: filterDropdownMaxWidth,
  minWidth,
  overflow: 'hidden !important',
  width: fullWidth ? minWidth : 'max-content',
  '--Paper-overlay': 'none',
  '--Paper-shadow': 'none',
});
